import React from 'react';

// In-memory route session shared by the rail CaptureDock and the narrow floating
// agent. Both presentations stay mounted on responsive pages; sharing the durable
// conversation fields here means a breakpoint swap reveals the same draft text,
// thread, staged confirmations and staged photo instead of a second empty agent.
// Transient device state (an active microphone, network spinner) remains local.
const sessions = new Map();
let turnId = 0;
export const ROUTE_AGENT_SESSION_TTL_MS = 15 * 60 * 1000;
export const ROUTE_AGENT_CONFIRM_TTL_MS = 5 * 60 * 1000;

export function nextRouteAgentTurnId() { turnId += 1; return `t${turnId}`; }

export function routeAgentSessionKey(context = {}) {
  let actor = context.actorId || context.role || 'staff';
  try { actor = window.OPDBridge?.currentUser?.id || actor; } catch (_) { /* non-browser test */ }
  const route = context.path || context.surface || 'app';
  // Never carry a conversation, staged confirmation, or staged photo from one
  // patient's route context into another. Presentation swaps at a breakpoint
  // share this exact key, while patient/encounter changes deliberately do not.
  const clinicalScope = context.patientId
    ? `patient:${context.patientId}`
    : context.encounterId ? `encounter:${context.encounterId}` : 'route';
  return `${actor}:${route}:${clinicalScope}`;
}

function storeFor(routeKey) {
  const key = String(routeKey || 'app');
  if (!sessions.has(key)) sessions.set(key, {
    snapshot: {}, defaults: {}, listeners: new Set(), updatedAt: Date.now(), fieldUpdatedAt: {}, expiryTimer: null,
  });
  const store = sessions.get(key);
  if (Date.now() - store.updatedAt > ROUTE_AGENT_SESSION_TTL_MS) {
    store.snapshot = { ...store.defaults };
    store.fieldUpdatedAt = {};
    store.updatedAt = Date.now();
  }
  return store;
}

function armExpiry(store) {
  if (store.expiryTimer) clearTimeout(store.expiryTimer);
  store.expiryTimer = setTimeout(() => {
    store.snapshot = { ...store.defaults };
    store.fieldUpdatedAt = {};
    store.updatedAt = Date.now();
    store.expiryTimer = null;
    for (const listener of store.listeners) listener();
  }, ROUTE_AGENT_SESSION_TTL_MS);
}

export function routeAgentFieldIsFresh(routeKey, field, maxAgeMs = ROUTE_AGENT_CONFIRM_TTL_MS) {
  const store = storeFor(routeKey);
  const at = store.fieldUpdatedAt[field];
  return Number.isFinite(at) && Date.now() - at <= maxAgeMs;
}

export function clearRouteAgentSession(routeKey) {
  const store = storeFor(routeKey);
  store.snapshot = { ...store.defaults };
  store.fieldUpdatedAt = {};
  store.updatedAt = Date.now();
  armExpiry(store);
  for (const listener of store.listeners) listener();
}

export function useRouteAgentField(routeKey, field, initialValue) {
  const store = storeFor(routeKey);
  if (!Object.prototype.hasOwnProperty.call(store.defaults, field)) {
    store.defaults = { ...store.defaults, [field]: typeof initialValue === 'function' ? initialValue() : initialValue };
  }
  if (!Object.prototype.hasOwnProperty.call(store.snapshot, field)) {
    store.snapshot = { ...store.snapshot, [field]: store.defaults[field] };
  }
  React.useEffect(() => { if (!store.expiryTimer) armExpiry(store); }, [store]);
  const subscribe = React.useCallback((listener) => {
    store.listeners.add(listener);
    return () => store.listeners.delete(listener);
  }, [store]);
  const getSnapshot = React.useCallback(() => store.snapshot[field], [store, field]);
  const value = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = React.useCallback((next) => {
    const current = store.snapshot[field];
    const valueNext = typeof next === 'function' ? next(current) : next;
    if (Object.is(current, valueNext)) return;
    store.snapshot = { ...store.snapshot, [field]: valueNext };
    const now = Date.now();
    store.updatedAt = now;
    store.fieldUpdatedAt = { ...store.fieldUpdatedAt, [field]: now };
    armExpiry(store);
    for (const listener of store.listeners) listener();
  }, [store, field]);
  return [value, setValue];
}

// ── Session task ledger (owner anti-duplicate rule, 2026-07-14 restoration) ──
// Every create_task the user CONFIRMS in this route session is recorded here.
// Two consumers: (1) the agent brain feeds it to the LLM as grounding ("tasks
// already recorded — judge duplicates against this"), (2) confirmAll's exact-
// match backstop (same title+patient+due+note+owner+priority → point at the
// existing FHIR Task instead of creating a twin). The entry retains the Task id
// and canonical Task/<id> reference returned by the confirmed write. Scoped by
// the route session key, so it never
// leaks across patients (the key embeds patient/encounter) and expires with
// the session TTL like every other field.
export function recordAgentSessionTask(routeKey, entry) {
  const store = storeFor(routeKey);
  const list = Array.isArray(store.snapshot.taskLedger) ? store.snapshot.taskLedger : [];
  store.snapshot = { ...store.snapshot, taskLedger: [...list, { ...entry, at: Date.now() }] };
  const now = Date.now();
  store.updatedAt = now;
  store.fieldUpdatedAt = { ...store.fieldUpdatedAt, taskLedger: now };
  armExpiry(store);
  for (const listener of store.listeners) listener();
}

export function readAgentSessionTasks(routeKey) {
  const store = storeFor(routeKey); // storeFor resets the snapshot when the session TTL lapsed
  return Array.isArray(store.snapshot.taskLedger) ? store.snapshot.taskLedger : [];
}

export const __routeAgentSessionTest = {
  sessions,
  reset() {
    for (const store of sessions.values()) if (store.expiryTimer) clearTimeout(store.expiryTimer);
    sessions.clear(); turnId = 0;
  },
};
